import axios from "axios";
import { getGatewayIdTokenFromAppToken } from "./gatewaySession";

interface AuthResponse {
  token: string;
  user: {
    id: string;
    email: string;
    name?: string;
    avatar?: string;
    accepts_terms?: boolean;
    accepts_privacy?: boolean;
  };
}

const mockAuthResponse: AuthResponse = {
  token: "mock-token",
  user: {
    id: "mock-id",
    email: "mock-email@example.com",
    name: "Mock User",
    avatar: "https://example.com/avatar.png",
  },
};

const temporaryResponse: AuthResponse = {
  token: "temporary-token",
  user: {
    id: "temporary-id",
    email: "temporary-email@example.com",
    name: "Temporary User",
    avatar: "https://example.com/temporary-avatar.png",
  },
};

export const authenticateWithBackend = async (
  firebaseToken: string
): Promise<AuthResponse> => {
  if (import.meta.env.VITE_USE_MOCK_API === "true") {
    console.log("Using mock authentication response");
    return new Promise((resolve) =>
      setTimeout(() => resolve(mockAuthResponse), 1000)
    );
  }

  try {
    const response = await axios.post<AuthResponse>(
      `${import.meta.env.VITE_AUTH_API}/api/auth/register`,
      {
        firebaseToken,
      },
      { headers: { Authorization: `Bearer ${firebaseToken}` } },
    );
    return response.data;
  } catch (error) {
    console.error("Error authenticating with backend", error);
    return temporaryResponse;
  }
};

// New function for checking authentication with the JWT token
export const checkAuthWithBackend = async (
  jwtToken: string
): Promise<AuthResponse> => {
  if (import.meta.env.VITE_USE_MOCK_API === "true") {
    console.log("Using mock check-auth response");
    return new Promise((resolve) =>
      setTimeout(() => resolve(mockAuthResponse), 1000)
    );
  }

  try {
    const firebaseToken = await getGatewayIdTokenFromAppToken(jwtToken);
    const response = await axios.get<AuthResponse>(
      `${import.meta.env.VITE_AUTH_API}/api/auth/check-auth`,
      {
        headers: {
          Authorization: `Bearer ${firebaseToken}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error checking authentication with backend", error);
    throw error;
  }
};

export const requestPasswordReset = async (email: string): Promise<void> => {
  try {
    await axios.post(
      `${import.meta.env.VITE_AUTH_API}/api/auth/request-password-reset`,
      {
        email,
      }
    );
    console.log(`Password reset request initiated for email: ${email}`);
  } catch (error) {
    console.error(`Error initiating password reset for email ${email}:`, error);
    throw error;
  }
};

export const resetPassword = async (
  token: string,
  newPassword: string
): Promise<void> => {
  try {
    await axios.post(
      `${import.meta.env.VITE_AUTH_API}/api/auth/reset-password`,
      {
        newPassword,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    console.log(`Password successfully reset.`);
  } catch (error) {
    console.error("Error resetting password:", error);
    throw error;
  }
};
